import * as React from "react";
import {
  makeStyles,
  shorthands,
  Text,
  Title2,
  Body1,
  Spinner,
} from "@fluentui/react-components";
import {
  BuildingRegular,
  ShieldCheckmarkRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#FFFFFF",
    ...shorthands.padding("32px", "24px"),
  },
  container: {
    maxWidth: "400px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  brandContainer: {
    marginBottom: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
  },
  brandIcon: {
    fontSize: "28px",
    color: "#0F6CBD",
  },
  brandText: {
    fontFamily: "Segoe UI Bold, -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 700,
    color: "#1F1F1F",
    fontSize: "20px",
    letterSpacing: "-0.01em",
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "80px",
    height: "80px",
    backgroundColor: "#F0F7FF",
    ...shorthands.borderRadius("50%"),
    marginBottom: "32px",
    ...shorthands.border("2px", "solid", "#D6E8FF"),
  },
  verifyIcon: {
    fontSize: "32px",
    color: "#0F6CBD",
  },
  spinner: {
    marginBottom: "24px",
  },
  title: {
    marginBottom: "16px",
    fontFamily: "Segoe UI Semibold, -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 600,
    color: "#1F1F1F",
    lineHeight: "1.3",
  },
  description: {
    fontFamily: "Segoe UI, -apple-system, BlinkMacSystemFont, sans-serif",
    color: "#616161",
    lineHeight: "1.5",
    marginBottom: "32px",
  },
  loadingSteps: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "flex-start",
    width: "100%",
    maxWidth: "280px",
  },
  step: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontFamily: "Segoe UI, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: "14px",
    color: "#424242",
  },
  stepIcon: {
    fontSize: "16px",
    color: "#0F6CBD",
  },
  stepText: {
    lineHeight: "1.4",
  },
});

interface LoadingPageProps {
  message?: string;
  userEmail?: string;
}

export const LoadingPage: React.FC<LoadingPageProps> = ({ 
  message = "Verifying Access", 
  userEmail 
}) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.brandContainer}>
          <BuildingRegular className={styles.brandIcon} />
          <Text className={styles.brandText}>ContraVault</Text>
        </div>

        <div className={styles.iconContainer}>
          <ShieldCheckmarkRegular className={styles.verifyIcon} />
        </div>

        <Spinner className={styles.spinner} size="large" />

        <Title2 className={styles.title}>
          {message}
        </Title2>

        <Body1 className={styles.description}>
          Please wait while we verify your access permissions...
        </Body1>

        <div className={styles.loadingSteps}>
          <div className={styles.step}>
            <div className={styles.stepIcon}>•</div>
            <span className={styles.stepText}>Checking user account</span>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>•</div>
            <span className={styles.stepText}>Validating company permissions</span>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>•</div>
            <span className={styles.stepText}>Verifying add-in access</span>
          </div>
        </div>

        {userEmail && (
          <Text
            style={{
              marginTop: "24px",
              fontSize: "13px",
              color: "#757575",
              fontFamily: "Segoe UI, -apple-system, BlinkMacSystemFont, sans-serif",
            }}
          >
            Verifying access for: {userEmail}
          </Text>
        )}
      </div>
    </div>
  );
};

export default LoadingPage;